import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Page } from '../Elements/PageMargins'
import SearchBar from '../Search/SearchBar'
import TitleContext from '../../TitleContext'
import { useTranslation } from 'react-i18next'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import BusinessContentCard from '../Card/BusinessContentCard'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import Box from '@mui/material/Box'
import { FileType } from '../Elements/FileTypeResource'
import { contentApi, ContentAsset } from '../../api/swagger'
import EmptyBusiness from './EmptyBusiness'
import { BusinessContentOptions } from '../../swagger/models/BusinessContentOptions'
import { buildFilterOptions } from '../../utils/buildFilterOptions'
import NoPermission from '../Elements/NoPermission'
import { useLocation, Outlet } from 'react-router'
import {
  Categories,
  Roles,
  FilterFileType,
} from '../../utils/searchAndFilterEnums'
import { styled } from '@mui/system'
import Header from '../Elements/Header'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { useMountEffect } from '../../hooks/useMountEffect'
import LoadingProgress from '../Elements/LoadingProgress'
import { useTheme } from '@mui/material/styles'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { useBusinessContext } from '../Context/BusinessContext'
import { escapeString } from '../../utils/stringUtility'
import { isFeaturedLearningPathAsset } from '../LearningCenter/utils/learningCenterUtils'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW } from '@utils/constants'

const SearchAndFilterWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(5, 3, 4, 0),
    flexDirection: 'column',
  },
}))

const SearchBarContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(0, 2, 0, 0),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    margin: 0,
    alignItems: 'center',
  },
}))

const BusinessCardWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginLeft: theme.spacing(-1),
  [theme.breakpoints.down('xs')]: {
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: 'unset',
  },
}))

const FilterContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(0, 2, 0, 0),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(1, 0),
  },
}))

const FilterDropdown = styled(DropDown)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0),
  },
}))

export const verifyTitle = (value: string, object: ContentAsset): string => {
  return !!value ? value : object.title || ''
}

export const sortAlphaNum = (a: ContentAsset, b: ContentAsset): number =>
  verifyTitle(a.learningPathTitle ?? '', a).localeCompare(
    verifyTitle(b.learningPathTitle ?? '', b),
    navigator.language,
    {
      numeric: true,
      sensitivity: 'base',
    }
  )

const BusinessTab: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const title = t('Business.Title', 'Business')
  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(title)
  const location = useLocation()
  const { handleError } = useAppSnackbar()
  const {
    Business: {
      fetchBusinessContentOptions: fetchBusinessContentOptionsLoadingId,
      fetchBusinessContent: fetchBusinessContentId,
    },
  } = useLoadingIds()

  const {
    state: routerState,
  }: {
    state?: {
      isAssetFavorited: {
        assetKey: number
        value: boolean
      }
    }
  } = location

  const { isAssetFavorited } = routerState || {}

  const [filterOptions, setFilterOptions] = useState<BusinessContentOptions>()

  const [businessContent, setBusinessContent] = useState<ContentAsset[]>([])

  const [error, setError] = useState<Error>()
  const [isClearFilters, setIsClearFilters] = useState(false)

  const genericErrorMessage = t(
    'Business.Error.FetchContent',
    'Error occurred retrieving business content.'
  )

  const isShowOnDesktop = useShowOnDesktop()
  const {
    search: searchQuery,
    type: filterByType,
    role: filterByRole,
    category: filterByCategory,
    updateSearch,
    updateRole,
    updateType,
    updateCategory,
    resetContextToDefaults,
    updatePage,
    resetPage,
    businessContentPerPage,
  } = useBusinessContext()

  const initialPageValue = useRef(businessContentPerPage)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  // Fetch Business Content (also used when filtering)
  const fetchContentByParams = async () => {
    // Build request body based on filters. If filter is set to 'All...' then do not include query param
    const body = {
      ...(filterByRole !== Roles.AllRoles && { role: filterByRole }),
      ...(filterByCategory !== Categories.AllCategories &&
        filterByCategory !== Categories.MyFavorites && {
          category: filterByCategory,
        }),
      ...(filterByType !== FilterFileType.AllTypes && {
        filetype: filterByType,
      }),
      ...(filterByCategory === Categories.MyFavorites && {
        showOnlyFavorites: true,
      }),

      ...(!!searchQuery &&
        searchQuery.length >= 3 && {
          search: escapeString(searchQuery).trim(),
        }),
    }
    try {
      const fetchedWidenAsset = await contentApi.fetchBusinessContent(body)
      setBusinessContent(fetchedWidenAsset.sort(sortAlphaNum))
    } catch (error) {
      setError(error as Error)
    }
  }
  const { triggerFetch: fetchContent, isLoading: isLoadingFetchContent } =
    useLoadingContext({
      asyncFunction: fetchContentByParams,
      loadingId: fetchBusinessContentId,
    })

  const hasFavoritedButtonBeenUpdated = useRef(false)

  const handleUpdateAssetStateByAssetKey = useCallback(
    ({
      assetKey,
      newState,
    }: {
      assetKey: number
      newState: Partial<ContentAsset>
    }) => {
      if (businessContent) {
        setBusinessContent((prevContent) =>
          prevContent.map((asset) =>
            asset.assetKey === assetKey ? { ...asset, ...newState } : asset
          )
        )
      }
    },
    [businessContent]
  )

  useEffect(() => {
    if (isAssetFavorited && !hasFavoritedButtonBeenUpdated.current) {
      handleUpdateAssetStateByAssetKey({
        assetKey: isAssetFavorited.assetKey,
        newState: {
          isFavorited: isAssetFavorited.value,
        },
      })
      hasFavoritedButtonBeenUpdated.current = true
      //Remove all values from state
      location.state = null
      return
    }
    hasFavoritedButtonBeenUpdated.current = false
  }, [handleUpdateAssetStateByAssetKey, isAssetFavorited, location])

  // Fetch all Filter Menu Options
  const fetchFilterMenuOptions = async () => {
    try {
      const fetchedFilterMenuOptions =
        await contentApi.fetchBusinessContentOptions()

      setFilterOptions(fetchedFilterMenuOptions)
    } catch (error) {
      setError(error as Error)
    }
  }

  const {
    triggerFetch: triggerFetchFilterMenuOptions,
    isLoading: isLoadingFetchFilterMenuOptions,
  } = useLoadingContext({
    asyncFunction: fetchFilterMenuOptions,
    loadingId: fetchBusinessContentOptionsLoadingId,
  })

  useMountEffect(() => {
    triggerFetchFilterMenuOptions()
    fetchContent()
  })

  useEffect(() => {
    triggerFetchFilterMenuOptions()
    fetchContent()
  }, [
    searchQuery,
    filterByRole,
    filterByCategory,
    filterByType,
    fetchContent,
    triggerFetchFilterMenuOptions,
  ])

  /**
   * If error display snack bar alert
   */
  useEffect(() => {
    if (!!error) {
      const handle = async () => await handleError(error, genericErrorMessage)
      handle()
    }
  })

  const handleSearch = (searchText: string) => {
    updateSearch(searchText)
    resetPage()
  }

  const handleDropDownSelection = (selection: string, id: string) => {
    switch (id) {
      case 'role':
        updateRole(selection as Roles)
        break
      case 'category':
        updateCategory(selection as Categories)
        break
      case 'type':
        updateType(selection as FilterFileType)
        break
    }
    resetPage()
  }

  const handleClearAllFilters = () => {
    resetContextToDefaults()
    setIsClearFilters(true)
  }

  const roleOptions = buildFilterOptions(
    filterOptions?.roles ?? [],
    'All Roles'
  )

  const categoryOptions = buildFilterOptions(
    filterOptions?.categories ?? [],
    'All Categories',
    'My Favorites'
  )

  const filetypeOptions = buildFilterOptions(
    filterOptions?.filetypes ?? [],
    'All Types'
  )

  const isLoading = isLoadingFetchContent || isLoadingFetchFilterMenuOptions

  useEffect(() => {
    if (
      businessContent.length > 0 &&
      initialPageValue.current === businessContentPerPage &&
      businessContentPerPage > ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW
    ) {
      if (!isLoading) {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [businessContent, businessContentPerPage, isLoading])

  if (businessContent.length === 0 && !!error && !isLoading) {
    return <EmptyBusiness isLoading={isLoading} />
  }

  if (!businessContent.length && !filterOptions?.roles.length && !isLoading) {
    return (
      <Page>
        <NoPermission
          content={t(
            'Business.NoPermission',
            `Sorry, you do not have permission to view this content.`
          )}
        />
      </Page>
    )
  }

  const inSearchState =
    !!searchQuery ||
    filterByCategory !== Categories.AllCategories ||
    filterByType !== FilterFileType.AllTypes ||
    filterByRole !== Roles.AllRoles

  return (
    <Page>
      {location.pathname === '/business' && (
        <>
          <SearchAndFilterWrapper>
            <SearchBarContainer>
              <SearchBar
                handleSearch={handleSearch}
                searchValue={searchQuery}
              />
            </SearchBarContainer>
            <FilterContainer>
              <FilterDropdown
                formControlProps={{
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                }}
                id="role"
                menuOptions={roleOptions.map((role) => {
                  return { name: role.name, id: role.id }
                })}
                defaultValue={t('Business.FilterOption.Roles', 'All Roles')}
                handleSelection={handleDropDownSelection}
                variant={DropDownVariant.SortAndFilter}
                value={filterByRole}
                isClearFilters={isClearFilters}
              />
            </FilterContainer>
            <FilterContainer>
              <FilterDropdown
                formControlProps={{
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                }}
                id="category"
                menuOptions={categoryOptions.map((category) => {
                  return { name: category.name, id: category.id }
                })}
                defaultValue={t(
                  'Business.FilterOption.Categories',
                  'All Categories'
                )}
                handleSelection={handleDropDownSelection}
                variant={DropDownVariant.SortAndFilter}
                value={filterByCategory}
                isClearFilters={isClearFilters}
              />
            </FilterContainer>
            <FilterContainer>
              <FilterDropdown
                formControlProps={{
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                }}
                id="type"
                menuOptions={filetypeOptions.map((filetype) => {
                  return { name: filetype.name, id: filetype.id }
                })}
                defaultValue={t('Business.FilterOption.Types', 'All Types')}
                handleSelection={handleDropDownSelection}
                variant={DropDownVariant.SortAndFilter}
                value={filterByType}
                isClearFilters={isClearFilters}
              />
            </FilterContainer>
            <FilterContainer>
              <OutlinedButton
                css={{ paddingBottom: '3px', paddingTop: '3px' }}
                id="clearAllFilters"
                variant={OutlinedButtonVariant.ClearAllFilters}
                onClick={handleClearAllFilters}
                fullWidth={!isShowOnDesktop}
              />
            </FilterContainer>
          </SearchAndFilterWrapper>
          {isLoading ? (
            <LoadingProgress />
          ) : (
            <>
              <Header
                component="h2"
                headerName={
                  inSearchState && businessContent.length === 1
                    ? `${businessContent.length} ${t(
                        'BusinessContent.SearchResult.ItemFound',
                        'item found'
                      )}`
                    : inSearchState
                    ? `${businessContent.length} ${t(
                        'BusinessContent.SearchResult.ItemsFound',
                        'items found'
                      )}`
                    : t('BusinessContent.Cards.All', 'All Business Content')
                }
              />
              <BusinessCardWrapper>
                {businessContent
                  .slice(0, businessContentPerPage)
                  .map((business) => {
                    const isFeaturedBusinessPathAsset =
                      isFeaturedLearningPathAsset({
                        learningPathTitle: business.learningPathTitle,
                        listingPriority: business.listingPriority,
                      })

                    return (
                      <Box
                        key={`${business.assetKey}-wrapper`}
                        ref={
                          businessContentPerPage >
                          ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW
                            ? bottomRef
                            : undefined
                        }
                      >
                        <BusinessContentCard
                          key={business.assetKey}
                          widenAssetKey={business.assetKey}
                          fileType={
                            isFeaturedBusinessPathAsset
                              ? FileType.LearningPath
                              : (business.filetype as FileType)
                          }
                          title={
                            isFeaturedBusinessPathAsset
                              ? (business.learningPathTitle as string)
                              : business.title
                          }
                          description={
                            isFeaturedBusinessPathAsset &&
                            !!business.learningPathDescription
                              ? business.learningPathDescription
                              : business.description ||
                                t(
                                  'BusinessContentCard.EmptyDescription',
                                  'No description available.'
                                )
                          }
                          isLearningPath={isFeaturedBusinessPathAsset}
                          isFavorite={!!business?.isFavorited}
                          viewOnly={!!business?.viewOnly}
                          isNdaRequired={!!business?.isNdaRequired}
                          ndaAcknowledged={!!business?.ndaAcknowledged}
                          handleFavoriteButtonClicked={() =>
                            handleUpdateAssetStateByAssetKey({
                              assetKey: business.assetKey,
                              newState: {
                                isFavorited: !business?.isFavorited,
                              },
                            })
                          }
                        />
                      </Box>
                    )
                  })}
              </BusinessCardWrapper>
              {/** Hide button once we are viewing all available Business Content */}
              {businessContent.slice(0, businessContentPerPage).length <
                businessContent.length && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  my={6}
                >
                  <OutlinedButton
                    data-testid="viewMoreButton"
                    id="viewMoreDraftEvents"
                    onClick={updatePage}
                    variant={OutlinedButtonVariant.ViewMore}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      {/* Render the children of business, anything with a route prefix of /business and also in the /business children. */}
      <Outlet />
    </Page>
  )
}
export default BusinessTab
